import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Form,
  Input,
  message,
  Select,
  Space,
  Steps,
  Tag,
  Tooltip,
  Upload,
} from 'antd'
import groupBy from 'lodash/groupBy'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { blue, redPrimary } from '../../../../color'
import { AvatarHasName } from '../../../../components/Common/Avatar'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup/SelectPeoplePopup'
import SpinnerInlineComponent from '../../../../components/SpinnerInlineComponent'
import {
  ASSIGNEE_TYPE,
  ELECTRONIC_DOCUMENT_STATUS,
} from '../../../../constants'
import validator from '../../../../validator'
import {
  FileAction,
  FileItem,
  SignDocumentList,
} from '../../WorkDocumentSignDocDetailPage/workDocumentSignDocDetailPageStyled'
import {
  CustomStepIcon,
  ModalCreateSignDocWrapper,
  StepsWrapper,
  UploadFileListItem,
} from './PopupUpdateSignDocStyled'

const { Step } = Steps
const { Option } = Select

const UPLOAD_BATCH_SIZE = 3

const dedupeFileList = fileList =>
  fileList.filter(
    (file, index, arr) => arr.findIndex(f => f.uid === file.uid) === index
  )

const uploadFilesInBatches = (
  fileStore,
  files,
  batchSize = UPLOAD_BATCH_SIZE
) => {
  const uploadBatch = async startIndex => {
    const batch = files.slice(startIndex, startIndex + batchSize)
    if (batch.length === 0) return []

    const results = await Promise.all(
      batch.map(file => {
        const formData = new FormData()
        formData.append('file', file)
        return fileStore.uploadFile(formData)
      })
    )

    const nextResults = await uploadBatch(startIndex + batchSize)
    return [...results, ...nextResults]
  }

  return uploadBatch(0)
}

const beforeUploadPdf = file => {
  const isPdf =
    file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')
  if (!isPdf) {
    message.error(`${file.name} không phải file PDF!`)
    return Upload.LIST_IGNORE
  }
  return false
}

const isMainFileSigned = file =>
  Boolean(
    file?.leader_signed ||
      file?.leaderSigned ||
      file?.lead_ktat_signed ||
      file?.leadKtatSigned ||
      file?.bod_signed ||
      file?.bodSigned ||
      file?.digital_signed ||
      file?.digitalSigned
  )

const PopupUpdateSignDoc = props => {
  const {
    loadingAnimationStore,
    electronicDocumentStore,
    fileStore,
    isVisiblePopupUpdateSignDoc,
    handleClosePopupUpdateSignDoc,
    selectPeopleStore,
    authenticationStore,
    signDoc,
  } = props

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    originDepartmentWithUserList,
  } = selectPeopleStore

  const [form] = Form.useForm()

  const { currentUser } = authenticationStore

  const [KTATList, setKTATList] = useState([])
  const [leadKTATList, setLeadKTATList] = useState([])
  const [BODList, setBODlist] = useState([])
  const [formApproverList, setFormApproverList] = useState([])
  const [leaderApproverList, setLeaderApproverList] = useState([])
  const [digitalSignerList, setDigitalSignerList] = useState([])
  const [releaserList, setReleaserList] = useState([])
  const [electronicDocument, setElectronicDocument] = useState([])
  const [stepsData, setStepsData] = useState([])

  const [fileList, setFileList] = useState([])
  const [annexAttachedFiles, setAnnexAttachedFiles] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)

  const {
    code,
    title,
    assignees,
    attachments,
    permission,
    status,
    annex_attachments,
  } = signDoc

  const creator = useMemo(
    () =>
      assignees?.filter(
        el =>
          el.assignee_type === ASSIGNEE_TYPE.USER &&
          el.permission === ASSIGNEE_TYPE.CREATE
      ),
    [assignees]
  )

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) return

    form.setFieldsValue({ title })

    if (signDoc.main_files?.length > 0) {
      const mainFiles = signDoc.main_files.map((file, index) => ({
        uid: file.file_id || `main-${index}`,
        name: file.file_name,
        id: file.file_id,
        originId: file.origin_file_id || file.file_id,
        type: 'application/pdf',
        isSigned: isMainFileSigned(file),
      }))
      setElectronicDocument(mainFiles)
      form.setFieldsValue({ electronic_document: mainFiles })
    }

    if (attachments?.length > 0) {
      setFileList(
        attachments.map((f, i) => ({
          uid: f.file_id || `${i}`,
          name: f.file_name,
          id: f.file_id,
        }))
      )
    }

    if (annex_attachments?.length > 0) {
      setAnnexAttachedFiles(
        annex_attachments.map((f, i) => ({
          uid: f.file_id || `${i}`,
          name: f.file_name,
          id: f.file_id,
        }))
      )
    }
  }, [
    isVisiblePopupUpdateSignDoc,
    title,
    attachments,
    annex_attachments,
    signDoc.main_files,
    form,
  ])

  const handleChangeElectronicDoc = useCallback(
    ({ fileList }) => {
      setElectronicDocument(prevFiles => {
        const uploadedFiles = dedupeFileList(fileList || [])
        const keptExisting = prevFiles.filter(file => file.id)
        const newFiles = uploadedFiles.filter(
          file =>
            file.originFileObj &&
            !keptExisting.some(existing => existing.uid === file.uid)
        )

        const combined = dedupeFileList([...keptExisting, ...newFiles]).map(
          file => ({
            ...file,
            uid: file.uid || file.id || `${file.name}-${Date.now()}`,
            name: file.name || file.file_name,
            id: file.id || file.file_id,
            type: file.type || 'application/pdf',
            originFileObj: file.originFileObj || file,
            isSigned: file.isSigned || isMainFileSigned(file),
          })
        )

        form.setFieldsValue({
          electronic_document: combined.length ? combined : undefined,
        })

        return combined
      })
    },
    [form]
  )

  const handleRemoveElectronicDoc = useCallback(
    uid => {
      setElectronicDocument(prevFileList => {
        const nextList = prevFileList.filter(file => file.uid !== uid)
        form.setFieldsValue({
          electronic_document: nextList.length ? nextList : undefined,
        })
        return nextList
      })
    },
    [form]
  )

  const allSteps = useMemo(
    () => [
      {
        key: 1,
        title: 'Tạo dự thảo',
        userList:
          creator && creator.length > 0
            ? [
                {
                  image_id: creator[0].image,
                  full_name: creator[0].name_uppercase,
                },
              ]
            : [],
        allowDeleted: false,
      },
      {
        key: 2,
        title: 'Trưởng đơn vị ký',
        userList: leaderApproverList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEADER_APPROVER,
        permission: ASSIGNEE_TYPE.LEADER_APPROVER,
      },
      {
        key: 3,
        title: 'P.KTAT duyệt thể thức',
        userList: KTATList || [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.KTAT_OFFICE,
        permission: ASSIGNEE_TYPE.KTAT_OFFICE,
      },
      {
        key: 4,
        title: 'TP.KTAT ký',
        userList: leadKTATList || [],
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
        permission: ASSIGNEE_TYPE.LEAD_KTAT_OFFICE,
      },
      {
        key: 5,
        title: 'Giám đốc ký',
        userList: BODList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
        permission: ASSIGNEE_TYPE.BOARD_OF_DIRECTOR,
      },
      {
        key: 6,
        title: 'Đóng dấu',
        userList: digitalSignerList || [],
        type: 'approval',
        allowDeleted: true,
        assignee_type: ASSIGNEE_TYPE.DIGITAL_SIGNER,
        permission: ASSIGNEE_TYPE.DIGITAL_SIGNER,
      },
      {
        key: 7,
        title: 'Phát hành',
        userList: releaserList || [],
        type: 'approval',
        allowDeleted: false,
        assignee_type: ASSIGNEE_TYPE.RELEASER,
        permission: ASSIGNEE_TYPE.RELEASER,
      },
    ],
    [
      creator,
      KTATList,
      leadKTATList,
      BODList,
      formApproverList,
      leaderApproverList,
      digitalSignerList,
      releaserList,
    ]
  )

  useEffect(() => {
    form.setFieldsValue({
      electronic_document:
        electronicDocument.length > 0 ? electronicDocument : undefined,
    })
  }, [electronicDocument])

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc || !permission || isInitialized) return

    const allowedPermissions = Array.isArray(permission) ? permission : (typeof permission === 'string' ? permission.split(',') : [])
    const filteredSteps = allSteps.filter(
      step => !step.permission || allowedPermissions.includes(step.permission)
    )

    setStepsData(filteredSteps)
    setIsInitialized(true)
  }, [isVisiblePopupUpdateSignDoc, permission, allSteps, isInitialized])

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) {
      setIsInitialized(false)
    }
  }, [isVisiblePopupUpdateSignDoc])

  useEffect(() => {
    if (!isInitialized) return

    setStepsData(prevSteps =>
      prevSteps.map(step => {
        switch (step.assignee_type) {
          case ASSIGNEE_TYPE.KTAT_OFFICE:
            return { ...step, userList: KTATList || [] }
          case ASSIGNEE_TYPE.LEAD_KTAT_OFFICE:
            return { ...step, userList: leadKTATList || [] }
          case ASSIGNEE_TYPE.BOARD_OF_DIRECTOR:
            return { ...step, userList: BODList || [] }
          case ASSIGNEE_TYPE.FORM_APPROVER:
            return { ...step, userList: formApproverList || [] }
          case ASSIGNEE_TYPE.LEADER_APPROVER:
            return { ...step, userList: leaderApproverList || [] }

          case ASSIGNEE_TYPE.DIGITAL_SIGNER:
            return { ...step, userList: digitalSignerList || [] }
          case ASSIGNEE_TYPE.RELEASER:
            return { ...step, userList: releaserList || [] }
          default:
            return step
        }
      })
    )
  }, [
    isInitialized,
    KTATList,
    leadKTATList,
    BODList,
    formApproverList,
    leaderApproverList,

    digitalSignerList,
    releaserList,
  ])

  useEffect(() => {
    ;(async () => {
      await selectPeopleStore.clearSelected()
    })()
  }, [])

  const {
    [ASSIGNEE_TYPE.USER]: allUsers = [],
    [ASSIGNEE_TYPE.DEPARTMENT]: departmentList = [],
    [ASSIGNEE_TYPE.GROUP]: groupList = [],
  } = useMemo(() => groupBy(assignees || [], 'assignee_type'), [assignees])

  const userList = useMemo(
    () => allUsers.filter(el => el.permission !== ASSIGNEE_TYPE.CREATE),
    [allUsers]
  )

  useEffect(() => {
    if (!isVisiblePopupUpdateSignDoc) return
    if (
      selectUserData[ASSIGNEE_TYPE.KTAT_OFFICE].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.LEAD_KTAT_OFFICE].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.BOARD_OF_DIRECTOR].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.FORM_APPROVER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER].length === 0 &&
      selectUserData[ASSIGNEE_TYPE.RELEASER].length === 0
    ) {
      selectPeopleStore.setSelectUserData(
        selectPeopleStore.convertSelectDataObj(userList)
      )
    }
  }, [originDepartmentWithUserList, isVisiblePopupUpdateSignDoc])

  useEffect(() => {
    setKTATList([...(selectUserData[ASSIGNEE_TYPE.KTAT_OFFICE] || [])])
    setLeadKTATList([...(selectUserData[ASSIGNEE_TYPE.LEAD_KTAT_OFFICE] || [])])
    setBODlist([...(selectUserData[ASSIGNEE_TYPE.BOARD_OF_DIRECTOR] || [])])
    setFormApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.FORM_APPROVER] || []),
    ])
    setLeaderApproverList([
      ...(selectUserData[ASSIGNEE_TYPE.LEADER_APPROVER] || []),
    ])
    setDigitalSignerList([
      ...(selectUserData[ASSIGNEE_TYPE.DIGITAL_SIGNER] || []),
    ])
    setReleaserList([...(selectUserData[ASSIGNEE_TYPE.RELEASER] || [])])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  const handleChangeFile = useCallback(
    info => {
      const newFileList = info.fileList.map(file => ({
        ...file,
        uid: file.uid || file.file_id || `${file.name}-${Date.now()}`,
        name: file.name || file.file_name,
        id: file.id || file.file_id,
        originFileObj: file.originFileObj || file,
      }))
      setFileList(newFileList)
    },
    [setFileList]
  )

  const handleChangeAnnexFile = useCallback(
    info => {
      const newFileList = info.fileList.map(file => ({
        ...file,
        uid: file.uid || file.file_id || `${file.name}-${Date.now()}`,
        name: file.name || file.file_name,
        id: file.id || file.file_id,
        originFileObj: file.originFileObj || file,
      }))
      setAnnexAttachedFiles(newFileList)
    },
    [setAnnexAttachedFiles]
  )

  const handleRemoveFile = useCallback(
    uid => {
      setFileList(prevFileList => prevFileList.filter(file => file.uid !== uid))
    },
    [setFileList]
  )

  const handleRemoveAnnexFile = useCallback(
    uid => {
      setAnnexAttachedFiles(prevFileList =>
        prevFileList.filter(file => file.uid !== uid)
      )
    },
    [setAnnexAttachedFiles]
  )

  const onFinish = useCallback(
    async values => {
      try {
        loadingAnimationStore.showSpinner(true)
        const uploadFileList = fileList
          .filter(
            (elem, index, fileList) =>
              fileList.findIndex(file => file.name === elem.name) === index
          )
          .filter(file => !file.id)
          .map(file => file.originFileObj)
          .filter(file => file)

        const uploadAnnexFileList = annexAttachedFiles
          .filter(
            (elem, index, fileList) =>
              fileList.findIndex(file => file.name === elem.name) === index
          )
          .filter(file => !file.id)
          .map(file => file.originFileObj)
          .filter(file => file)

        // Upload tài liệu trình ký MỚI
        const uploadSignDocList = electronicDocument
          .filter(file => !file.id) // Chỉ upload file chưa có ID
          .map(file => file.originFileObj)
          .filter(file => file)

        const [
          responseSignDocs,
          responseAttachments,
          responseAnnexAttachments,
        ] = await Promise.all([
          uploadFilesInBatches(fileStore, uploadSignDocList),
          uploadFilesInBatches(fileStore, uploadFileList),
          uploadFilesInBatches(fileStore, uploadAnnexFileList),
        ])

        const signDocFileIds = responseSignDocs.map(res => res.data.file_id)

        // ✅ Tính toán new_items và remove_items ĐÚNG
        const new_items_main_files = []
        const remove_items_main_files = []

        // Lấy danh sách file ID gốc
        const originMainFiles = toJS(signDoc.main_files || []).map(
          file => file.file_id
        )

        // Lấy danh sách file ID sau khi update
        // = files đã upload mới + files cũ còn giữ lại
        const afterMainFiles = [
          ...signDocFileIds, // Files vừa upload
          ...electronicDocument
            .filter(file => file.id) // Files cũ còn trong danh sách
            .map(file => file.id),
        ]

        // Files mới thêm vào
        afterMainFiles.forEach(fileId => {
          if (!originMainFiles.includes(fileId)) {
            new_items_main_files.push(fileId)
          }
        })

        // Files bị xóa đi
        originMainFiles.forEach(fileId => {
          if (!afterMainFiles.includes(fileId)) {
            remove_items_main_files.push(fileId)
          }
        })

        console.log('Main Files Update:', {
          origin: originMainFiles,
          after: afterMainFiles,
          new_items: new_items_main_files,
          remove_items: remove_items_main_files,
        })

        const new_items_assign = []
        const remove_items_assign = []
        const listAssignOrigin = [
          ...userList,
          ...departmentList,
          ...groupList,
        ].map(el => ({
          assignee_code: el.assignee_code,
          assignee_type: el.assignee_type,
          permission: el.permission,
        }))

        const currentStepPermissions = new Set(
          stepsData.filter(step => step.permission).map(step => step.permission)
        )

        const userListByPermission = {
          [ASSIGNEE_TYPE.KTAT_OFFICE]: KTATList,
          [ASSIGNEE_TYPE.LEAD_KTAT_OFFICE]: leadKTATList,
          [ASSIGNEE_TYPE.BOARD_OF_DIRECTOR]: BODList,
          [ASSIGNEE_TYPE.FORM_APPROVER]: formApproverList,
          [ASSIGNEE_TYPE.LEADER_APPROVER]: leaderApproverList,
          [ASSIGNEE_TYPE.DIGITAL_SIGNER]: digitalSignerList,
          [ASSIGNEE_TYPE.RELEASER]: releaserList,
        }

        const listAssignAfter = Object.entries(userListByPermission)
          .filter(([permission]) => currentStepPermissions.has(permission))
          .flatMap(([permission, userList]) =>
            userList.map(el => ({
              assignee_code: el.id,
              assignee_type: el.assignee_type,
              permission: el.permission || permission,
            }))
          )

        listAssignAfter.forEach(assignAfter => {
          if (
            !listAssignOrigin.some(
              assignOrigin =>
                assignOrigin.assignee_code + assignOrigin.permission ===
                assignAfter.assignee_code + assignAfter.permission
            )
          ) {
            new_items_assign.push({
              assignee_code: assignAfter.assignee_code,
              assignee_type: assignAfter.assignee_type,
              permission: assignAfter.permission,
            })
          }
        })

        listAssignOrigin.forEach(assignOrigin => {
          if (
            !listAssignAfter.some(
              assignAfter =>
                assignAfter.assignee_code + assignAfter.permission ===
                assignOrigin.assignee_code + assignOrigin.permission
            )
          ) {
            remove_items_assign.push({
              assignee_code: assignOrigin.assignee_code,
              assignee_type: assignOrigin.assignee_type,
              permission: assignOrigin.permission,
            })
          }
        })

        const permission = stepsData
          .filter(el => el.key !== 1)
          .map(el => el.permission)
          .join(',')

        const attachmentFileIds = responseAttachments.map(
          res => res.data.file_id
        )
        const annexFileIds = responseAnnexAttachments.map(
          res => res.data.file_id
        )

        // Handle attachments (tài liệu đính kèm)
        const new_items_attachments = []
        const remove_items_attachments = []
        const originFiles = toJS(attachments).map(file => file.file_id)
        const afterFiles = [
          ...attachmentFileIds,
          ...fileList.filter(file => file.id).map(file => file.id),
        ]

        afterFiles.forEach(fileId => {
          if (!originFiles.includes(fileId)) {
            new_items_attachments.push(fileId)
          }
        })

        originFiles.forEach(fileId => {
          if (!afterFiles.includes(fileId)) {
            remove_items_attachments.push(fileId)
          }
        })

        // Handle annex attachments (phụ lục đi kèm)
        const new_items_annex = []
        const remove_items_annex = []
        const originAnnexFiles = toJS(annex_attachments || []).map(
          file => file.file_id
        )
        const afterAnnexFiles = [
          ...annexFileIds,
          ...annexAttachedFiles.filter(file => file.id).map(file => file.id),
        ]

        afterAnnexFiles.forEach(fileId => {
          if (!originAnnexFiles.includes(fileId)) {
            new_items_annex.push(fileId)
          }
        })

        originAnnexFiles.forEach(fileId => {
          if (!afterAnnexFiles.includes(fileId)) {
            remove_items_annex.push(fileId)
          }
        })

        // Update assignees
        const dataSubmitAssign = {
          new_items: new_items_assign,
          remove_items: remove_items_assign,
          action: status,
        }
        await electronicDocumentStore.updateAssignPerson(code, dataSubmitAssign)

        // Update document with attachments and annexes
        const submitData = {
          title: values.title,
          main_files: {
            new_items: new_items_main_files,
            remove_items: remove_items_main_files,
          },
          attachments: {
            new_items: new_items_attachments,
            remove_items: remove_items_attachments,
          },
          annex_attachments: {
            new_items: new_items_annex,
            remove_items: remove_items_annex,
          },
          permission: permission,
          action: status,
        }

        await electronicDocumentStore.updateElectronicDocument(code, submitData)

        form.resetFields()
        setElectronicDocument([])
        setFileList([])
        setAnnexAttachedFiles([])
        selectPeopleStore.clearSelected()
        electronicDocumentStore.clearFilter()

        await electronicDocumentStore.getElectronicDocumentById(code)
        handleClosePopupUpdateSignDoc()
        message.success('Chỉnh sửa thành công!')
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    },
    [
      electronicDocument,
      fileList,
      annexAttachedFiles,
      KTATList,
      leadKTATList,
      BODList,
      formApproverList,
      leaderApproverList,
      digitalSignerList,
      releaserList,
      stepsData,
      selectUserData,
      code,
      status,
      attachments,
      annex_attachments,
      userList,
      departmentList,
      groupList,
      fileStore,
      electronicDocumentStore,
      selectPeopleStore,
      form,
      handleClosePopupUpdateSignDoc,
      loadingAnimationStore,
    ]
  )

  const handleRemoveStep = useCallback(
    key => {
      const removedStep = stepsData.find(step => step.key === key)

      if (removedStep && removedStep.assignee_type) {
        selectPeopleStore.clearSelectedByType(removedStep.assignee_type)

        switch (removedStep.assignee_type) {
          case ASSIGNEE_TYPE.KTAT_OFFICE:
            setKTATList([])
            break
          case ASSIGNEE_TYPE.LEAD_KTAT_OFFICE:
            setLeadKTATList([])
            break
          case ASSIGNEE_TYPE.BOARD_OF_DIRECTOR:
            setBODlist([])
            break
          case ASSIGNEE_TYPE.FORM_APPROVER:
            setFormApproverList([])
            break
          case ASSIGNEE_TYPE.LEADER_APPROVER:
            setLeaderApproverList([])
            break
          case ASSIGNEE_TYPE.DIGITAL_SIGNER:
            setDigitalSignerList([])
            break
          case ASSIGNEE_TYPE.RELEASER:
            setReleaserList([])
            break
        }
      }

      setStepsData(prevSteps => prevSteps.filter(step => step.key !== key))
    },
    [stepsData, selectPeopleStore]
  )

  const existingStepKeys = useCallback(() => {
    return stepsData.map(step => step.key)
  }, [stepsData])

  const handleAddStep = useCallback(
    key => {
      if (!key) return
      const stepToAdd = allSteps.find(step => step.key === key)
      if (stepToAdd) {
        const newStep = {
          ...stepToAdd,
          userList: [],
        }

        setStepsData(prev => {
          const newSteps = [...prev, newStep]
          newSteps.sort((a, b) => a.key - b.key)
          return newSteps
        })
      }
    },
    [allSteps]
  )

  const renderTitleStep = useMemo(
    () => level => (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{level.title}</span>
        {level.key !== 1 && (
          <Tooltip title="Chọn người dùng" color={blue}>
            <Button
              style={{ marginLeft: 8 }}
              icon={<UserOutlined style={{ color: blue }} />}
              onClick={() =>
                handleOpenSelectUserDepartmentPopup(level.assignee_type)
              }
            />
          </Tooltip>
        )}
      </div>
    ),
    []
  )

  const renderDescStep = useMemo(
    () => level => {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: level.allow_edit ? 6 : 14,
            marginBottom: 14,
          }}>
          {level.userList[0] ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag
                className={'tag-selected'}
                color={'blue'}
                closable={level.key === 1 ? false : true}
                onClose={() =>
                  selectPeopleStore.handleRemoveSelect(level.userList[0])
                }>
                <AvatarHasName
                  imgId={level.userList[0]?.image_id}
                  size={22}
                  name={level.userList[0]?.full_name}
                />
              </Tag>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag style={{ borderRadius: 10 }}>Chưa gán người!</Tag>
            </div>
          )}
        </div>
      )
    },
    []
  )

  const deletableOptions = useMemo(() => {
    const existingKeys = existingStepKeys()
    return allSteps
      .filter(item => item.allowDeleted)
      .map(step => (
        <Option
          key={step.key}
          value={step.key}
          disabled={existingKeys.includes(step.key)}>
          {step.title}
        </Option>
      ))
  }, [allSteps, existingStepKeys])

  const renderedFileList = useMemo(() => {
    return fileList.map((file, index) => (
      <FileItem key={file.uid}>
        <span>
          <FileTextOutlined style={{ marginRight: 5 }} />
          {file.name}
        </span>
        <FileAction>
          <Tooltip title="Xóa">
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: redPrimary }} />}
              onClick={() => handleRemoveFile(file.uid)}
            />
          </Tooltip>
        </FileAction>
      </FileItem>
    ))
  }, [fileList, handleRemoveFile])

  const renderedAnnexFileList = useMemo(() => {
    return annexAttachedFiles.map((file, index) => (
      <FileItem key={file.uid}>
        <span>
          <FileTextOutlined style={{ marginRight: 5 }} />
          {file.name}
        </span>
        <FileAction>
          <Tooltip title="Xóa">
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: redPrimary }} />}
              onClick={() => handleRemoveAnnexFile(file.uid)}
            />
          </Tooltip>
        </FileAction>
      </FileItem>
    ))
  }, [annexAttachedFiles, handleRemoveAnnexFile])

  const renderedSteps = useMemo(() => {
    return stepsData.slice(0, 4).map(level => (
      <Step
        key={level.key}
        title={renderTitleStep(level)}
        description={renderDescStep(level)}
        icon={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomStepIcon>{level.key}</CustomStepIcon>
            {/* {level.allowDeleted && (
              <CloseOutlined
                style={{ marginLeft: 6, fontSize: 12, cursor: 'pointer' }}
                onClick={() => handleRemoveStep(level.key)}
              />
            )} */}
          </div>
        }
      />
    ))
  }, [stepsData, renderTitleStep, renderDescStep, handleRemoveStep])

  const renderedSteps2 = useMemo(() => {
    if (stepsData.length <= 4) return null
    return stepsData.slice(4).map(level => (
      <Step
        key={level.key}
        title={renderTitleStep(level)}
        description={renderDescStep(level)}
        icon={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomStepIcon>{level.key}</CustomStepIcon>
          </div>
        }
      />
    ))
  }, [stepsData, renderTitleStep, renderDescStep, handleRemoveStep])

  const handleClose = useCallback(() => {
    form.resetFields()
    setElectronicDocument([])
    setFileList([])
    setAnnexAttachedFiles([])
    selectPeopleStore.clearSelected()
    handleClosePopupUpdateSignDoc()
  }, [form, selectPeopleStore, handleClosePopupUpdateSignDoc])

  return (
    <>
      <ModalCreateSignDocWrapper
        visible={isVisiblePopupUpdateSignDoc}
        footer={null}
        closable={false}
        title={'Chỉnh sửa'}
        width={1000}
        style={{ top: 60 }}>
        <SpinnerInlineComponent />
        <Form
          onFinish={onFinish}
          layout={'vertical'}
          name={'form'}
          form={form}
          initialValues={{ title: title }}>
          <Form.Item
            label={'Tiêu đề:'}
            name={'title'}
            rules={[
              { required: true, message: ' Vui lòng nhập tiêu đề trình ký!' },
              { validator: validator.validateInputString },
              { max: 200, message: 'Tiêu đề không được dài quá 200 kí tự' },
            ]}>
            <Input placeholder={'Nhập tiêu đề...'} />
          </Form.Item>
          {(status === ELECTRONIC_DOCUMENT_STATUS.PENDING ||
            status === ELECTRONIC_DOCUMENT_STATUS.CHANGE_DOC ||
            status === ELECTRONIC_DOCUMENT_STATUS.KTAT_OFFICE_REJECTION ||
            status === ELECTRONIC_DOCUMENT_STATUS.LEAD_REJECTION ||
            status === ELECTRONIC_DOCUMENT_STATUS.ROLLBACK_TO_CREATOR ||
            status === ELECTRONIC_DOCUMENT_STATUS.TP_REVERT) && (
            <>
              <Form.Item
                label={'Tài liệu trình ký:'}
                name={'electronic_document'}
                rules={[
                  {
                    validator: () =>
                      electronicDocument.length
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(' Vui lòng chọn tài liệu trình ký!')
                          ),
                  },
                ]}>
                <Upload
                  fileList={electronicDocument}
                  multiple
                  beforeUpload={beforeUploadPdf}
                  onChange={handleChangeElectronicDoc}
                  showUploadList={false}
                  accept={'application/pdf,.pdf'}>
                  <Button icon={<UploadOutlined />}>
                    Chọn tài liệu trình ký
                  </Button>
                </Upload>
              </Form.Item>

              <SignDocumentList $scrollable={electronicDocument.length > 5}>
                {electronicDocument.map(file => (
                  <UploadFileListItem
                    key={file.uid || file.id}
                    isSigned={file.isSigned}
                    isNotPDF={file.type !== 'application/pdf'}>
                    <FileTextOutlined />
                    <span>{file.name}</span>
                    {!file.isSigned && (
                      <Tooltip title={'Xóa'}>
                        <Button
                          type={'text'}
                          icon={
                            <DeleteOutlined style={{ color: redPrimary }} />
                          }
                          onClick={() => handleRemoveElectronicDoc(file.uid)}
                        />
                      </Tooltip>
                    )}
                  </UploadFileListItem>
                ))}
              </SignDocumentList>
            </>
          )}

          <Form.Item label={'Tài liệu đính kèm:'} name={'file'}>
            <Upload
              valuePropName={'fileList'}
              fileList={fileList}
              multiple={true}
              beforeUpload={() => false}
              onChange={handleChangeFile}
              showUploadList={false}>
              <Button icon={<UploadOutlined />}>Chọn tài liệu đính kèm</Button>
            </Upload>
          </Form.Item>
          <SignDocumentList $scrollable={fileList.length > 5}>
            {renderedFileList}
          </SignDocumentList>

          <StepsWrapper count={stepsData.length} i={4}>
            <Steps
              key={stepsData
                .slice(0, 4)
                .map(s => s.key + (s.userList[0]?.id || ''))
                .join('-')}
              status="process"
              className={'steps'}
              labelPlacement={'vertical'}
              size={'small'}>
              {stepsData.slice(0, 4).map(level => (
                <Step
                  key={level.key}
                  title={renderTitleStep(level)}
                  description={renderDescStep(level)}
                  icon={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CustomStepIcon>{level.key}</CustomStepIcon>
                    </div>
                  }
                />
              ))}
            </Steps>

            {stepsData.length > 4 && (
              <Steps
                key={stepsData
                  .slice(4)
                  .map(s => s.key + (s.userList[0]?.id || ''))
                  .join('-')}
                status="process"
                className={'steps'}
                labelPlacement={'vertical'}
                size={'small'}
                style={{ marginTop: 20 }}>
                {stepsData.slice(4).map(level => (
                  <Step
                    key={level.key}
                    title={renderTitleStep(level)}
                    description={renderDescStep(level)}
                    icon={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CustomStepIcon>{level.key}</CustomStepIcon>
                      </div>
                    }
                  />
                ))}
              </Steps>
            )}
          </StepsWrapper>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 20,
            }}>
            <Space>
              <Button danger icon={<CloseOutlined />} onClick={handleClose}>
                Hủy bỏ
              </Button>
              <Button
                htmlType={'submit'}
                icon={<EditOutlined />}
                type={'primary'}>
                Chỉnh sửa
              </Button>
            </Space>
          </div>
        </Form>

        <SelectPeoplePopup
          onlyOneUser={true}
          handleCancelSelectUser={handleCancelSelectUserDepartment}
          handleSubmitSelectUser={() =>
            setIsVisibleSelectUserDepartmentPopup(false)
          }
          isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
        />
      </ModalCreateSignDocWrapper>
    </>
  )
}

PopupUpdateSignDoc.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'fileStore',
    'electronicDocumentStore',
    'selectPeopleStore',
    'signedDocumentStore',
    'authenticationStore'
  )(observer(PopupUpdateSignDoc))
)
